import DOMPurify from "dompurify";

const escHtml = (str = "") =>
  String(str).replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch]));

export function isRichHtml(value = "") {
  return /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
}

export function legacyTextToHtml(value = "") {
  const safe = escHtml(value || "");
  const withBold = safe.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  const lines = withBold.split(/\n/);
  const out = [];
  let listMode = null;

  const flushList = () => {
    if (listMode === "ul") out.push("</ul>");
    if (listMode === "ol") out.push("</ol>");
    listMode = null;
  };

  const ensureList = (type) => {
    if (listMode === type) return;
    flushList();
    out.push(type === "ol" ? "<ol>" : "<ul>");
    listMode = type;
  };

  for (const line of lines) {
    const trimmed = line.trimStart();
    const ordered = trimmed.match(/^(\d+)\.\s+(.*)$/);
    const bullet = trimmed.startsWith("- ");

    if (ordered) {
      ensureList("ol");
      out.push(`<li>${ordered[2]}</li>`);
      continue;
    }

    if (bullet) {
      ensureList("ul");
      out.push(`<li>${trimmed.slice(2)}</li>`);
      continue;
    }

    if (!trimmed.trim()) {
      flushList();
      out.push("<br/>");
      continue;
    }

    flushList();
    out.push(`<p>${line}</p>`);
  }

  flushList();
  return out.join("\n");
}

export function richTextToEditorValue(value = "") {
  const source = String(value || "");
  if (!source.trim()) return "";
  return isRichHtml(source) ? source : legacyTextToHtml(source);
}

export function richTextToHtml(value = "") {
  const source = richTextToEditorValue(value);
  if (!source) return "";

  return DOMPurify.sanitize(source, {
    USE_PROFILES: { html: true },
  });
}

export function richTextToPlainText(value = "") {
  const html = richTextToHtml(value);
  if (!html) return "";

  if (typeof document !== "undefined") {
    const node = document.createElement("div");
    node.innerHTML = html;
    return String(node.innerText || node.textContent || "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|blockquote|h[1-6])>/gi, "\n")
    .replace(/<li>/gi, "- ")
    .replace(/<[^>]+>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function isRichTextEmpty(value = "") {
  return !richTextToPlainText(value).trim();
}

/** Põe *…* / _…_ / ~…~ à volta do texto, com os espaços fora (o WhatsApp não formata «*texto *»). */
function waWrap(text, mark) {
  if (!text.trim()) return text;
  return text
    .split("\n")
    .map((line) => {
      const core = line.trim();
      if (!core) return line;
      const lead = line.match(/^\s*/)[0];
      const trail = line.match(/\s*$/)[0];
      return `${lead}${mark}${core}${mark}${trail}`;
    })
    .join("\n");
}

/** Texto de um elemento em linha, com a formatação do WhatsApp. */
function waInline(node) {
  if (node.nodeType === 3) return node.nodeValue.replace(/ /g, " ");
  if (node.nodeType !== 1) return "";
  const tag = node.tagName.toLowerCase();
  if (tag === "br") return "\n";
  const inner = Array.from(node.childNodes).map(waInline).join("");
  if (tag === "strong" || tag === "b") return waWrap(inner, "*");
  if (tag === "em" || tag === "i") return waWrap(inner, "_");
  if (tag === "s" || tag === "strike" || tag === "del") return waWrap(inner, "~");
  return inner;
}

const cleanLine = (s) => s.replace(/[ \t]+/g, " ").replace(/ *\n */g, "\n").trim();

/**
 * Blocos do editor: { kind: "title" | "p" | "li" | "quote", text }.
 * Títulos (H1/H2) e parágrafos curtos todos a negrito contam como títulos.
 */
function waBlocks(root) {
  const blocks = [];
  const pushText = (kind, text) => {
    const t = cleanLine(text);
    if (t) blocks.push({ kind, text: t });
  };
  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType === 3) {
      pushText("p", node.nodeValue);
      continue;
    }
    if (node.nodeType !== 1) continue;
    const tag = node.tagName.toLowerCase();
    if (/^h[1-6]$/.test(tag)) {
      const t = cleanLine(waInline(node).replace(/\*/g, ""));
      if (t) blocks.push({ kind: "title", text: `*${t}*` });
    } else if (tag === "ul" || tag === "ol") {
      let n = 0;
      for (const li of Array.from(node.children)) {
        const listType = li.getAttribute("data-list") || (tag === "ol" ? "ordered" : "bullet");
        const indent = Number((li.className.match(/ql-indent-(\d)/) || [])[1] || 0);
        n = listType === "ordered" ? n + 1 : n;
        const mark = listType === "ordered" ? `${n}.` : "•";
        const t = cleanLine(waInline(li));
        if (t) blocks.push({ kind: "li", text: `${"   ".repeat(indent)}${mark} ${t}` });
      }
    } else if (tag === "blockquote") {
      const t = cleanLine(waInline(node));
      if (t) blocks.push({ kind: "quote", text: t.split("\n").map((l) => `> ${l}`).join("\n") });
    } else {
      const t = cleanLine(waInline(node));
      if (!t) continue;
      // Parágrafo curto todo a negrito (ex.: «18 de Outubro — Contribuições»): é um título.
      const title = t.length <= 90 && /^\*[^*]+\*:?$/.test(t);
      blocks.push({ kind: title ? "title" : "p", text: t });
    }
  }
  return blocks;
}

/**
 * HTML do editor → texto do WhatsApp, pela estrutura do editor:
 * parágrafos separados por uma linha em branco, títulos a *negrito* (com o
 * texto logo a seguir), listas com • ou 1., e *negrito* / _itálico_ / ~riscado~.
 */
export function richTextToWhatsApp(value = "") {
  const html = richTextToHtml(value);
  if (!html) return "";
  if (typeof document === "undefined") return richTextToWhatsAppFallback(html);

  const root = document.createElement("div");
  root.innerHTML = html;
  const blocks = waBlocks(root);
  let out = "";
  blocks.forEach((b, i) => {
    const prev = blocks[i - 1];
    // Linha em branco antes de cada título e entre parágrafos; o texto de um título e os itens de uma lista seguem-se.
    if (prev) {
      const together = b.kind !== "title" && (prev.kind === "title" || (b.kind === "li" && prev.kind === "li"));
      out += together ? "\n" : "\n\n";
    }
    out += b.text;
  });
  return out.trim();
}

/** Sem DOM (ex.: testes em Node): conversão simples por expressões regulares. */
function richTextToWhatsAppFallback(html) {
  let s = String(html)
    .replace(/\r\n/g, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/h[1-6]>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<\/?(ul|ol)[^>]*>/gi, "\n")
    .replace(/<(strong|b)(\s[^>]*)?>/gi, "*")
    .replace(/<\/(strong|b)>/gi, "*")
    .replace(/<(em|i)(\s[^>]*)?>/gi, "_")
    .replace(/<\/(em|i)>/gi, "_")
    .replace(/<\/?u(\s[^>]*)?>/gi, "")
    .replace(/<\/?blockquote[^>]*>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/\*\(\s*\*|\*\s*\*\)/g, "")
    .replace(/(^|\s)\*\s*\*(\s|$)/g, "$1$2")
    .replace(/(^|\s)_\s*_(\s|$)/g, "$1$2")
    .trim();

  return s;
}
